import React, { useEffect, useState, useContext } from 'react'
import { components, constants, useServices, useTranslation } from 'cng-web-lib'
import moment from 'moment-timezone'
import {
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core'
import clsx from 'clsx'
import ResponseApiUrls from 'src/apiUrls/AceOcnResponseApiUrls'
import NaCodeMaintenanceApiURLs from "src/apiUrls/NaCodeMaintenanceApiUrls"
import Namespace from 'src/constants/locale/Namespace'
import AceOcnBillofladingKeys from 'src/constants/locale/key/AceOcnBilloflading'

const {
  table: { useFetchCodeMaintenanceLookup }
} = components

const {
  filter: { EQUAL }
} = constants

const useStyles = makeStyles((theme) => ({
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: theme.palette.background.sectionOddBg,
      '& .MuiTableCell-head': {
        borderBottom: 0,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.2,
        textTransform: 'uppercase'
      }
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        '&:last-child': {
          '& .MuiTableCell-root': {
            borderBottom: 0
          }
        }
      }
    },
    '& .MuiTableCell-root': {
      padding: '8px 16px'
    }
  }
}))

function StatusNotification(props) {
  const { id, isViewOnly = false } = props

  const [data, setData] = useState([])
  const [lookups, setLookups] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const { fetchRecord, fetchRecords } = useServices()
  const [dispositionCodeDescriptionByCode, setDispositionCodeDescriptionByCode] = useState({})
  const { translate } = useTranslation([Namespace.ACE_OCN_BILLOFLADING])
  const dispositionCodeDescNotAvailable = translate(
    Namespace.ACE_OCN_BILLOFLADING,
    AceOcnBillofladingKeys.DISPOSITION_CODE_DESC_NOT_AVAILABLE
  )
  const fetchCodeMaintenanceLookup = useFetchCodeMaintenanceLookup()
  const classes = useStyles()

  function fetchAceOnStatusNotification() {
    return fetchRecords.execute(ResponseApiUrls.GET_SN,
      {
        filters: [
          {
            field: 'manifestId',
            operator: 'equal',
            value: id
          }
        ]
      }, (res) => res)
  }

  function showAceOCnStatusNotification() {
    Promise.all([
      fetchAceOnStatusNotification(),
      fetchRecords.execute(
        NaCodeMaintenanceApiURLs.GET_NOT_IN_NA,
        {
          customData: { codeMType: "CodeMasterCode" },
          filters: [
            // Fetch active + inactive so previously-received codes still show description in tooltip
            { field: 'codeType', operator: EQUAL, value: 'ACE_OCN_DISPOSITION_CODE' }
          ],
          sorts: [{ field: 'id', direction: 'ASC' }]
        },
        (res) => res.content,
        (error) => console.log(error)
      )
    ]).then(([respData, dispositionCodes]) => {
      setData(respData)

      const descriptionByCode = {}
      ;(dispositionCodes || []).forEach((item) => {
        descriptionByCode[item.code] = item.descriptionEn || item.description || item.descriptionFr || ''
      })
      setDispositionCodeDescriptionByCode(descriptionByCode)
    })
  }

  function renderDispositionCodeCell(rowData) {
    const code = rowData?.dispositionCode
    if (!code) return ''

    const normalizedCode = String(code).trim()
    const description = dispositionCodeDescriptionByCode[code] || dispositionCodeDescriptionByCode[normalizedCode]

    return (
      <Tooltip
        title={
          description ||
          dispositionCodeDescNotAvailable 
        }
        arrow
        placement="top"
      >
        <span>{code}</span>
      </Tooltip>
    )
  }

  useEffect(() => {
    showAceOCnStatusNotification();
    // Promise.all([
    //   fetchCodeMaintenanceLookup(
    //     constants.CodeMaintenanceType.CODE_MASTER,
    //     undefined,
    //     [
    //       {
    //         field: 'codeType',
    //         operator: EQUAL,
    //         value: 'ACI_HW_SUBMISSION_TYPE'
    //       }
    //     ],
    //     undefined,
    //     'code'
    //   ),
    //   fetchCodeMaintenanceLookup(
    //     constants.CodeMaintenanceType.CODE_MASTER,
    //     undefined,
    //     [{ field: 'codeType', operator: EQUAL, value: 'ACIHWY_STATUS' }],
    //     undefined,
    //     'code'
    //   )
    // ]).then(([submissionType, status]) => {
    //   setLookups({ submissionType, status })
    // })
  }, [])

  const columns = [
    {
      field: 'cbpbatchNo',
      title: "CBP BATCH NO"
    },
    {
      field: 'cbpRemarks',
      title: "CBP REMARKS"
    },
    {
      field: 'blNumber',
      title: "BILL NUMBER"
    },
    {
      field: 'dispositionCode',
      title: "DISPOSITION CODE",
      render: renderDispositionCodeCell
    },
    {
      field: 'quantity',
      title: "SN QTY"
    },
    {
      field: 'totalPackages',
      title: "BL QTY"
    },
    {
      field: 'entityNo',
      title: "ENTITY NUMBER"
    },
    {
      field: 'entryType',
      title: "ENTRY TYPE"
    },
    {
      field: 'entryNo',
      title: "ENTRY NUMBER"
    },
    {
      field: 'actionDateTime',
      title: "ACTION DATE",
      render: (data) =>
        data.actionDateTime &&
        moment(data.actionDateTime).tz("Canada/Eastern").format('D MMM YYYY HH:mm')
    },
    {
      field: 'cbpProcessingDate',
      title: "CBP PROCESSING DATE",
      render: (data) =>
        data.cbpProcessingDate &&
        moment(data.cbpProcessingDate).tz("Canada/Eastern").format('D MMM YYYY HH:mm')
    }
  ]

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value)
    setPage(0)
  }

  return (
    <>
      <TableContainer>
        <Table className={clsx(classes.table, 'new-ui-table-component__table')}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell key={index}>{column.title}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((d) => (
                  <TableRow key={d.id}>
                    {columns.map((column, index) => {
                      let cellValue = d[column.field];
                      
                      if (column.field === "quantity") {
                        cellValue = String(cellValue).replace(/^0+/, '');
                      }
                      
                      return (
                        <TableCell key={index}>
                          {column.render ? column.render(d) : cellValue}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell align='center' colSpan={columns.length}>
                  <Typography variant='body2' color='textSecondary'>
                    No records to display
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100, 500, 2000]}
        component='div'
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  )
}

export default StatusNotification
